import { Alert, Button, Form } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router";
import { useLocation, useSearchParam, useToggle } from "react-use";
import { AuthenticationStatus, AuthorizeRequest, AuthorizeWebResponse } from "../api/types";
import { authorizeLoginCookieVerify, authorizeLogoutPostV2, silent, unwrap } from "../api";
import { linkEGate } from "../helpers/eGateApi";
import LoginStyler from "./Login.style";
import useApp from "../hooks/useApp";
import classNames from "classnames";
import LoginFooter from "./LoginFooter";
import PasswordInput from "./PasswordInput";
import logo from "../image/main-logo.svg";
import Entities from "../Entities";
import { getDefaultView } from "../helpers/utils";
import qs from "qs";
import { GoogleLogin } from "@react-oauth/google";
import AppleLogin from "./AppleLogin";
import useServerInfo from "../hooks/useServerInfo";
import TfaChallenge from "./TfaChallenge";
/**
 * Web Login Component
 */
function LoginIn() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useApp();

  const [form] = Form.useForm();

  //apple/google login status
  const redirectLoginStatus = useSearchParam("status");
  //apple/google login description
  const redirectLoginDescription = useSearchParam("description");

  const [loginError, setLoginError] = useState<AuthenticationStatus>();
  const [loginErrorDescription, setLoginErrorDescription] = useState<string>();
  const [loginLoading, toggleLoginLoading] = useToggle(false);
  const [tfaPendingUser, setTfaPendingUser] = useState<string | null>(null);

  const locationState = useLocation();

  const [prevUrl, setPrevUrl] = useState("");

  const from = locationState?.state?.usr;
  useEffect(() => {
    if (from) {
      setPrevUrl(from);
    }
  }, [from]);

  const entities = Object.values(Entities);

  function getRolesByRoute(view: string) {
    const viewParsed = view.replace("/app/", "").split("/")[0];
    return entities.find((i) => i.baseRoute == viewParsed)?.roleView;
  }

  /**
   * Can view page that the user was redirected from into the login page
   * @param view
   * @param authResponse
   */
  function canViewPage(view: string, authResponse: AuthorizeWebResponse) {
    if (!view) {
      return undefined;
    }
    const rolesByRoute = getRolesByRoute(view) ?? [];
    const hasAnyRoleLogin = (...roles: string[]) => roles.some((r) => authResponse?.roles?.includes(r));
    return hasAnyRoleLogin(...rolesByRoute);
  }

  /**
   * Get redirect after login
   * Determines the right page for the account
   * @param auth
   */
  function getRedirect(auth: AuthorizeWebResponse): string {
    const canView = canViewPage(prevUrl, auth);

    if (!canView) {
      return getDefaultView(auth) ?? "/app/profile";
    }
    if (prevUrl) {
      return prevUrl;
    }

    return getDefaultView(auth) ?? "/app/profile";
  }

  async function completeLogin(authorizeWebResponse: AuthorizeWebResponse) {
    // The login response is built before the session cookie exists, so customer-filtered data such as features
    // comes back empty; the verify call runs as the signed-in user
    const verified = await unwrap(authorizeLoginCookieVerify({ ...silent })).catch(() => undefined);
    const auth = verified?.user ? verified : authorizeWebResponse;

    void login(auth);
    const redirect = getRedirect(auth);
    navigate(redirect);
  }

  async function cancelTfa() {
    // Clear the partial-auth cookies before returning to the password form so
    // the next /login/totp call can't reuse them.
    try {
      await unwrap(authorizeLogoutPostV2({ ...silent }));
    } catch {
      // Ignore — we just want the user back at the password form.
    }
    setTfaPendingUser(null);
    setLoginError(undefined);
    setLoginErrorDescription(undefined);
  }

  /**
   * Submit form callback
   * @param values
   */
  async function onFinish(values: AuthorizeRequest) {
    try {
      toggleLoginLoading();
      const url = linkEGate("authorize/login");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      const response = await fetch(url + "/?culture=" + i18n.language, {
        method: "POST",
        body: JSON.stringify(values),
        headers,
      });

      if (response.status === 200 || response.status === 401) {
        const body: AuthorizeWebResponse = await response.json();
        if (body.status === "confirm_by_tfa") {
          // Server may return either 200 or 401 alongside confirm_by_tfa;
          // partial-auth cookies are issued in both cases.
          setTfaPendingUser(body.user ?? values.username ?? "");
        } else if (response.status === 200) {
          await completeLogin(body);
        } else {
          setLoginError(body.status);
          setLoginErrorDescription(body.description);
        }
      }
      toggleLoginLoading(false);
    } catch (e) {
      console.error(e);
      setLoginError("invalid");
      setLoginErrorDescription(t("Unknown error") as string);

      toggleLoginLoading();
    }
  }

  function getLoginErrorMessage() {
    return loginErrorDescription;
  }

  const location = useLocation();
  const { data: serverInfo } = useServerInfo();
  const appleClientId = serverInfo?.appleClientId;
  if (location.search) {
    if (
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
      }).status == "Success"
    ) {
      return <Navigate to={"/app/dashboard"} />;
    }
  }

  if (tfaPendingUser !== null) {
    return (
      <LoginStyler>
        <div className="login-content">
          <div className="logo-wrapper">
            <img alt="eDigi logo" src={logo} width="128px" />
          </div>
          <TfaChallenge
            username={tfaPendingUser}
            onCancel={() => void cancelTfa()}
            onSuccess={completeLogin}
          />
        </div>
        <LoginFooter />
      </LoginStyler>
    );
  }

  return (
    <LoginStyler>
      <div className="login-content">
        <div className="logo-wrapper">
          <img alt="eDigi logo" src={logo} width="128px" />
        </div>
        <Form form={form} onFinish={(values: any) => onFinish(values as AuthorizeRequest)}>
          <Form.Item
            className="isoInputWrapper"
            name={"username"}
            noStyle
            rules={[{ required: true, message: t("Please input your email!") as string }]}
            shouldUpdate
          >
            <input disabled={loginLoading} placeholder={t("E-Mail")} />
          </Form.Item>
          <Form.Item
            className="isoInputWrapper"
            name={"password"}
            noStyle
            rules={[
              {
                required: true,
                message: t("Please input your password!") as string,
              },
            ]}
            shouldUpdate
          >
            <PasswordInput
              autocomplete={"current-password"}
              disabled={loginLoading}
              id="password"
              placeholder={t("Password")}
            />
          </Form.Item>
          <Form.Item className="isoInputWrapper" noStyle shouldUpdate>
            <Button
              className={classNames("login-button", {})}
              disabled={loginLoading}
              htmlType="submit"
              id="btnLogin"
              loading={loginLoading}
              type="primary"
            >
              {!loginLoading && t("Login")}
            </Button>
          </Form.Item>
          <GoogleLogin
            login_uri={linkEGate("authorize/google", null, "v2")}
            shape={"pill"}
            size={"medium"}
            text={"signin_with"}
            theme={"outline"}
            ux_mode={"redirect"}
            onSuccess={() => {}}
          />
          {appleClientId ? <AppleLogin clientId={appleClientId} /> : null}
          {loginError && <Alert message={getLoginErrorMessage()} type={"error"} />}

          {redirectLoginStatus && redirectLoginStatus != "Success" && (
            <Alert message={redirectLoginDescription ?? t("Login failed")} type={"error"} />
          )}

          <Link to="/reset-password">{t("Forgot password?")}</Link>
        </Form>
      </div>
      <LoginFooter />
    </LoginStyler>
  );
}

export default LoginIn;
